import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import TableActionButton from "@components/buttons/TableActionButton";
import { primeServices } from "@services/apis/Prime";
import { AssignAgentToTradeRequest } from "@services/models/Prime/AssignAgentToTradeRequest";
import { ChatMessageData } from "@services/models/Prime/ChatHistoryModel";
import {
  GetDisputedTradeListData,
  GetDisputedTradesListRequest,
  GetDisputedTradesListResponse,
} from "@services/models/Prime/GetDisputedTradeListModel";
import webSocketService from "@services/websocket/WebsocketServices";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import ConstantFunctions from "../../../utils/constants_functions";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import PrimeDetailsTabs from "../PrimeDetailsTabs";
import PrimeDisputeChat from "./components/PrimeDisputeChat";

const PrimeDisputedList = () => {
  const constantFunctions = new ConstantFunctions();
  const gridRef = useRef<AgGridReact>(null);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const [disputed, setDisputes] = useState<GetDisputedTradeListData[]>([]);
  const [userId, setUserId] = useState<number>();
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tradeId, setTradeId] = useState<string | undefined>(undefined);
  const [tradeDataForChat, setTradeDataForChat] = useState<
    GetDisputedTradeListData | undefined
  >(undefined);
  const [tradeType, setTradeType] = useState<string[] | undefined>(undefined);
  const [chatNotifications, setChatNotifications] = useState<Set<number>>(
    new Set(),
  );

  const handleSolveDispute = async (tradeIdParam: number) => {
    const assignAgentRequest: AssignAgentToTradeRequest = {
      tradeId: tradeIdParam,
    };
    try {
      const data = await primeServices.assignAgentToTrade(assignAgentRequest);
      handleSuccess({ message: data.msg });
    } catch (error: any) {
      handleError({
        message: error?.response?.data?.msg || "Failure",
      });
    }
  };
  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Disputed on",
        field: "createdAt",
        flex: 1.2,
        minWidth: 140,
        maxWidth: 200,
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Agent ID",
        field: "agentId",
        flex: 1,
        minWidth: 100,
        maxWidth: 150,
        valueFormatter: (params) => params.value || "- -",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Trade ID",
        field: "tradeId",
        flex: 1,
        minWidth: 100,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center h-full">
              <span className="mr-1 text-[10px] text-text-base font-medium">
                {data.tradeId}
              </span>
              <span
                className={`text-[10px] font-medium px-[6px] h-[24px] rounded flex items-center ${
                  data.tradeType === "B"
                    ? "bg-bg-status-green"
                    : "bg-bg-status-red"
                } text-text-base`}
              >
                {data.tradeType}
              </span>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Payment Method",
        field: "paymentMethod",
        flex: 1.1,
        minWidth: 120,
        maxWidth: 180,
        valueFormatter: (params) => params.value || "- -",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Buyer",
        field: "buyer",
        flex: 1.5,
        minWidth: 170,
        maxWidth: 250,
        cellRenderer: (params: any) => {
          const { data } = params;
          const displayName =
            data.buyer.length > 12
              ? `${data.buyer.substring(0, 12)}...`
              : data.buyer;
          return (
            <div className="flex items-center h-full">
              <span className="mr-1 text-[10px] text-text-base font-medium">
                {displayName}
              </span>
              {data.cashier === "Buyer" && (
                <span className="text-[10px] font-medium px-[6px] h-[24px] rounded flex items-center justify-center bg-bg-button-secondary text-text-base">
                  C
                </span>
              )}
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Seller",
        field: "seller",
        flex: 1.5,
        minWidth: 170,
        maxWidth: 250,
        cellRenderer: (params: any) => {
          const { data } = params;
          const displayName =
            data.seller.length > 12
              ? `${data.seller.substring(0, 12)}...`
              : data.seller;
          return (
            <div className="flex items-center h-full">
              <span className="mr-1 text-[10px] text-text-base font-medium">
                {displayName}
              </span>
              {data.cashier === "Seller" && (
                <span className="text-[10px] font-medium px-[6px] h-[24px] rounded flex items-center justify-center bg-bg-button-secondary text-text-base">
                  C
                </span>
              )}
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Amount",
        field: "amount",
        flex: 1.3,
        minWidth: 140,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center h-full text-[10px] text-text-base font-medium">
              <div className="flex items-center">
                <img
                  src={data.cryptoCurrencyImage}
                  alt=""
                  className="h-[12px] w-[12px] mr-1"
                />
                <span>{data.cryptoAmount?.toFixed(2)}</span>
              </div>
              <span className="mx-1">|</span>
              <div className="flex items-center">
                <img
                  src={data.fiatCurrencyImage}
                  alt=""
                  className="h-[12px] w-[12px] mr-1"
                />
                <span>{data.fiatAmount?.toFixed(2)}</span>
              </div>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
      },
      {
        headerName: "Action",
        field: "action",
        flex: 0.7,
        minWidth: 120,
        maxWidth: 160,
        cellRenderer: (params: any) => {
          const { data } = params;
          const hasNotification = chatNotifications.has(data.tradeId);

          if (data.agentId !== userId?.toString()) {
            return (
              <div className="flex items-center h-full">
                <TableActionButton
                  variant="primary"
                  icon="support_agent"
                  disabled={Boolean(data.agentId)}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSolveDispute(data.tradeId);
                  }}
                >
                  Solve
                </TableActionButton>
              </div>
            );
          } else {
            return (
              <div className="flex items-center h-full">
                <button
                  className="bg-bg-button-secondary border border-border-button-secondary text-[14px] text-text-base font-semibold py-[2px] px-[6px] rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setTradeDataForChat(data);
                    setTradeId(undefined);
                    setIsPanelCollapsed(false);
                    setChatNotifications((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(data.tradeId);
                      return newSet;
                    });
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div className="relative flex items-center">
                    <span className="material-symbols-outlined text-icon-base text-[12px] align-middle">
                      chat_bubble
                    </span>
                    {hasNotification && (
                      <div className="absolute top-[-4px] right-[-4px] w-[8px] h-[8px] bg-bg-button-delete rounded-full"></div>
                    )}
                  </div>
                </button>
              </div>
            );
          }
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        suppressCellSelection: true,
        cellStyle: { cursor: "default" },
      },
    ],
    [constantFunctions, userId, chatNotifications, handleSolveDispute],
  );

  const onRowClicked = (event: RowClickedEvent) => {
    // Check if the click originated from the Action column (chat button or solve button)
    const target = event.event?.target as HTMLElement;
    if (
      target &&
      (target.closest("button") ||
        target.closest('.ag-cell[col-id="action"]')?.querySelector("button"))
    ) {
      return; // Don't handle row click if it's from a button in the Action column
    }

    const selectedData = event.data as GetDisputedTradeListData;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setTradeId(selectedData.tradeId.toString());
    setTradeDataForChat(undefined);
    setIsPanelCollapsed(false);
  };

  const fetchDisputedTrades = async () => {
    const data: GetDisputedTradesListRequest = {
      tradeType: tradeType ? tradeType : undefined,
    };
    try {
      setIsLoading(true);
      const response: GetDisputedTradesListResponse =
        await primeServices.getDisputedTradesList(data);
      setDisputes(response.data);
      setUserId(response.userId);
    } catch (error: any) {
      setError(error.message || "Failed to fetch prime orders.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputedTrades();
  }, [tradeType]);

  useEffect(() => {
    const jwt = secureLocalStorage.getItem("jwt");
    webSocketService.connect({ token: jwt }, true);

    webSocketService.protectedSocket?.on("connect", () => {});
  }, []);

  const handleTradeChangeWebSockets = (
    socketData: GetDisputedTradeListData,
  ) => {
    setDisputes((prevDisputedTrades) => {
      const existingTradeIndex = prevDisputedTrades.findIndex(
        (trade) => trade.tradeId === socketData.tradeId,
      );

      if (existingTradeIndex !== -1) {
        const updatedTrades = [...prevDisputedTrades];
        updatedTrades[existingTradeIndex] = socketData;
        return updatedTrades;
      } else {
        return [socketData, ...prevDisputedTrades];
      }
    });
  };

  const handleSocketRemove = (socketData: { tradeId: string }) => {
    const tradeId = Number(socketData.tradeId);
    if (!isNaN(tradeId)) {
      setDisputes((prevDisputedTrades) =>
        prevDisputedTrades.filter((trade) => trade.tradeId !== tradeId),
      );
    }
  };

  const handleP2PChatMessage = (data: ChatMessageData) => {
    if (tradeDataForChat?.tradeId !== data.tradeId) {
      setChatNotifications((prev) => {
        const newSet = new Set(prev);
        newSet.add(data.tradeId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.onMessage(
          "admin_trade_update_dispute",
          true,
          handleTradeChangeWebSockets,
        );
        webSocketService.onMessage(
          "admin_trade_update_dispute_resolved",
          true,
          handleSocketRemove,
        );
        webSocketService.onMessage(
          "admin_p2p_ramp_chat",
          true,
          handleP2PChatMessage,
        );
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };

    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected]);

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px]">
        <div className="mr-4 flex w-full flex-row items-center justify-between">
          <p className="flex text-text-base text-[14px] font-semibold items-center">
            Prime Dispute Resolution
          </p>
          <LightCustomButton
            text="SOP/MESSAGE TEMPLATES DOC"
            isActive={true}
            onClick={() => {
              window.open(
                "https://docs.google.com/document/d/17OYuPmjNqM0BNmOPaklzDZ6ZaRyvs3U8LCxpZxiJfoY/edit?usp=sharing",
              );
            }}
          />
        </div>
      </div>
      <div className="flex flex-row items-start">
        <div
          className={`flex flex-col bg-bg-page-body py-[20px] px-[12px] overflow-hidden ${
            tradeId && !isPanelCollapsed ? "w-3/4" : "w-full"
          }`}
          style={{ height: "calc(100dvh - 70px)" }}
        >
          {/* Filter Row */}
          <div className="flex justify-start space-x-3 mb-4 items-center">
            <p className="text-text-base text-[10px] font-bold">Filter by:</p>
            <div className="flex space-x-3">
              {["Buy", "Sell"].map((type) => (
                <div
                  key={type}
                  onClick={() =>
                    setTradeType(
                      (prev) =>
                        prev?.includes(type)
                          ? prev.filter((s) => s !== type) // Toggle off
                          : [...(prev || []), type], // Add if not present
                    )
                  }
                  className={`cursor-pointer border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                    tradeType?.includes(type)
                      ? "border-border-button-secondary bg-bg-button-secondary"
                      : "border-border-subtle bg-bg-page-body"
                  }`}
                >
                  {type}
                </div>
              ))}
            </div>
          </div>
          <div className="overflow-hidden" style={{ height: "100%" }}>
            {isLoading && disputed.length === 0 ? (
              <AgGridSkeleton
                columnDefs={columnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <AgGridReactTable
                ref={gridRef}
                rowData={disputed}
                columnDefs={columnDefs}
                onRowClicked={onRowClicked}
                getRowId={(params) => params.data.tradeId.toString()}
                error={error}
              />
            )}
          </div>
        </div>
        {tradeId && (
          <div
            className={`bg-bg-page-body border-l border-border-subtle transition-all duration-300 relative ${
              isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
            }`}
            style={{ height: "calc(100dvh - 70px)" }}
          >
            <button
              onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
              className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
              style={{
                left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                width: "32px",
                height: "60px",
              }}
              aria-label={isPanelCollapsed ? "Expand panel" : "Collapse panel"}
            >
              <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                {isPanelCollapsed ? "chevron_left" : "chevron_right"}
              </span>
            </button>

            <div
              className={`overflow-auto h-full transition-opacity duration-300 pt-[16px] ${
                isPanelCollapsed
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
              }`}
            >
              <PrimeDetailsTabs tradeId={tradeId} countryFilter="IN" />
            </div>
          </div>
        )}
        {tradeDataForChat && (
          <PrimeDisputeChat
            tradeData={tradeDataForChat}
            userId={userId!}
            onDisputeResoultion={(tradeId) => {
              setTradeDataForChat(undefined);
              setTradeId(undefined);
            }}
          />
        )}
      </div>
    </>
  );
};

export default PrimeDisputedList;
